import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, Image, TouchableOpacity, Pressable, Platform, Dimensions, ActivityIndicator, Linking } from 'react-native';
import TopGreenHeader from '../Common/TopGreenHeader';
import {
  CalendarIcon,
  ClockIcon,
  BadgeCheckIcon,
  InfoIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DownloadIcon,
  TicketIcon,
} from '../../components/EventIcons';
import { useRoute } from '@react-navigation/native';
import axios from 'axios';
import Toast from 'react-native-toast-message';
import RNFS from 'react-native-fs';
import RenderHtml, { defaultSystemFonts } from 'react-native-render-html';
import API_PATHS from '../../apiCalls/ApiPath';
import AddParticipantModal from '../../components/AddParticipantModal';
import QrCodeView from '../../components/QrCodeView';

const WIDE_BREAKPOINT = 720;

// The app-wide green, shared with the header bar and the primary buttons on the
// other screens. GREEN_INK is the darker shade kept for green text on white,
// where #A6CE39 is too light to read.
const GREEN = '#A6CE39';
const GREEN_INK = '#5F8F22';
const INK = '#171720';
const MUTED = '#8B8B96';
const HAIRLINE = '#EBEBF2';
const CHIP_BG = '#EEF0FC';
const CHIP_BORDER = '#E1E4F8';
const AMBER = '#E2A83C';
const DANGER = '#D9534F';

const STATUS_COLORS = {
  ongoing: '#B7410E',
  upcoming: GREEN,
  completed: '#556B2F',
};

const cardShadow = Platform.select({
  web: { boxShadow: '0 1px 2px rgba(23,23,32,0.04), 0 8px 24px rgba(23,23,32,0.05)' },
  default: {
    shadowColor: '#0F1226',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.06,
    shadowRadius: 14,
    elevation: 3,
  },
});

const badgeShadow = Platform.select({
  web: { boxShadow: '0 4px 14px rgba(23,23,32,0.18)' },
  default: {
    shadowColor: '#0F1226',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.18,
    shadowRadius: 8,
    elevation: 3,
  },
});

const SessionRow = ({ icon, label, value, extra, last }) => (
  <View style={[styles.infoRow, last && styles.infoRowLast]}>
    <View style={styles.infoIconChip}>{icon}</View>
    <View style={styles.seatsColumn}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue}>{value}</Text>
      {extra}
    </View>
  </View>
);

// `eventDate` is a UTC-midnight marker for a calendar day, so every formatter
// below reads it in UTC — otherwise a viewer west of GMT sees the previous day.
const toDate = (epochSeconds) => new Date(epochSeconds * 1000);

const formatDateLong = (epochSeconds) =>
  epochSeconds == null
    ? '\u2014'
    : toDate(epochSeconds).toLocaleDateString('default', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });

const formatDateCompact = (epochSeconds) =>
  epochSeconds == null
    ? '\u2014'
    : toDate(epochSeconds).toLocaleDateString('default', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });

const formatDateMedium = (epochSeconds) =>
  epochSeconds == null
    ? '\u2014'
    : toDate(epochSeconds).toLocaleDateString('default', { month: 'short', day: 'numeric', timeZone: 'UTC' });

const formatWeekdayChip = (epochSeconds) =>
  epochSeconds == null
    ? ''
    : toDate(epochSeconds).toLocaleDateString('default', { weekday: 'short', timeZone: 'UTC' }).toUpperCase();

const formatDayNumber = (epochSeconds) =>
  epochSeconds == null ? '--' : String(toDate(epochSeconds).getUTCDate()).padStart(2, '0');

// The viewer's own calendar day, expressed as the same UTC-midnight marker the
// API uses for eventDate, so the two can be compared directly.
const todayMarker = () => {
  const now = new Date();
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 1000;
};

const DAY_STATE_BADGE = {
  today: { bg: GREEN, fg: '#fff', label: 'TODAY' },
  next: { bg: '#EAF3DC', fg: GREEN_INK, label: 'UP NEXT' },
  past: { bg: '#EFEFF3', fg: MUTED, label: 'PAST' },
};

const formatMoney = (amount) =>
  `$${(Number(amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// A child can sit on more than one registration for the same day, so participant
// counts are de-duplicated by kidId rather than summed across tickets.
const collectKids = (registrations) => {
  const seen = new Set();
  const kids = [];
  registrations.forEach((reg) =>
    (reg.tickets || []).forEach((ticket) =>
      (ticket.kids || []).forEach((kid) => {
        if (kid?.kidId && !seen.has(kid.kidId)) {
          seen.add(kid.kidId);
          kids.push(kid);
        }
      }),
    ),
  );
  return kids;
};

const formatTicketType = (type) => {
  if (!type) return '';
  return type
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const PARTIAL_PILL_BG = '#FDF3D6';
const PARTIAL_PILL_FG = '#9A6B12';

const PAYMENT_PILL_META = {
  PAID: { bg: '#EAF3DC', fg: GREEN_INK, label: 'Paid' },
  COMPLETED: { bg: '#EAF3DC', fg: GREEN_INK, label: 'Paid' },
  SUCCESS: { bg: '#EAF3DC', fg: GREEN_INK, label: 'Paid' },
  PENDING: { bg: '#FBE3E2', fg: DANGER, label: 'Due' },
  DUE: { bg: '#FBE3E2', fg: DANGER, label: 'Due' },
  FAILED: { bg: '#FBE3E2', fg: DANGER, label: 'Due' },
  PARTIAL: { bg: PARTIAL_PILL_BG, fg: PARTIAL_PILL_FG, label: 'Partial Paid' },
  PARTIAL_PAID: { bg: PARTIAL_PILL_BG, fg: PARTIAL_PILL_FG, label: 'Partial Paid' },
  'PARTIAL PAID': { bg: PARTIAL_PILL_BG, fg: PARTIAL_PILL_FG, label: 'Partial Paid' },
  PARTIALLY_PAID: { bg: PARTIAL_PILL_BG, fg: PARTIAL_PILL_FG, label: 'Partial Paid' },
  CANCELLED: { bg: '#FBE3E2', fg: DANGER, label: 'Cancelled' },
};

// While an invoice is still fully outstanding, another booking is folded into it
// via edit-event-registration. Once it is paid (or part-paid) the invoice is
// closed to changes, so a follow-up booking becomes its own registration.
const UNPAID_INVOICE_STATUSES = ['DUE', 'PENDING', 'FAILED'];

const isRegistrationUnpaid = (registration) =>
  UNPAID_INVOICE_STATUSES.includes(
    String(registration?.invoice?.status || registration?.paymentStatus || '').toUpperCase(),
  );

const paymentPillMeta = (status) =>
  PAYMENT_PILL_META[String(status || '').toUpperCase()] || {
    bg: '#EFEFF3',
    fg: MUTED,
    label: String(status || 'Unknown'),
  };

const RegistrationCard = ({ registration, isWide, onDownloadQr, onPayInvoice, onNewRegistration, downloading }) => {
  const invoice = registration.invoice || {};
  const pill = paymentPillMeta(invoice.status || registration.paymentStatus);
  const isInvoiceDue = String(invoice.status || '').toLowerCase() === 'due';
  const addsToThisInvoice = isRegistrationUnpaid(registration);
  const tickets = registration.tickets || [];
  const totalKids = tickets.reduce((sum, t) => sum + (t.kids?.length || 0), 0);
  const date = registration.eventDate || {};

  return (
    <View style={styles.ticketCardOuter}>
      {/* Stub head — booking summary, date, total */}
      <View style={styles.ticketHead}>
        <View style={styles.ticketHeadLeft}>
          <View style={styles.ticketHeadIconChip}>
            <TicketIcon size={18} color={GREEN_INK} strokeWidth={2} />
          </View>
          <View style={styles.ticketHeadText}>
            <Text style={styles.ticketTitleText} numberOfLines={1}>
              {tickets.length} {tickets.length === 1 ? 'Ticket' : 'Tickets'} · {totalKids}{' '}
              {totalKids === 1 ? 'kid' : 'kids'}
            </Text>
            <Text style={styles.ticketTypeText}>
              {formatDateLong(date.eventDate)}
              {date.startTime ? ` · ${date.startTime} - ${date.endTime}` : ''}
            </Text>
          </View>
        </View>

        <View style={styles.ticketPriceBlock}>
          <Text style={styles.ticketPriceValue}>${registration.totalAmount}</Text>
        </View>
      </View>

      {/* Perforation */}
      <View style={styles.ticketPerfWrap}>
        <View style={[styles.ticketNotch, styles.ticketNotchLeft]} />
        <View style={styles.ticketPerfLine} />
        <View style={[styles.ticketNotch, styles.ticketNotchRight]} />
      </View>

      {/* Body — one block per ticket */}
      <View style={styles.ticketBody}>
        {tickets.map((ticket) => {
          const kids = ticket.kids || [];
          return (
            <View key={ticket.registrationTicketId} style={styles.regTicketBlock}>
              <View style={styles.regTicketTitleRow}>
                <Text style={styles.regTicketTitle} numberOfLines={1}>
                  {ticket.title || 'Ticket'}
                </Text>
                <Text style={styles.regTicketPrice}>${ticket.ticketPrice}</Text>
              </View>
              <Text style={styles.ticketTypeText}>{formatTicketType(ticket.ticketType)}</Text>

              <View style={styles.ticketGuestList}>
                {kids.map((kid) => (
                  <View key={kid.kidId} style={styles.ticketGuestRow}>
                    <Image
                      source={kid.profilePic ? { uri: kid.profilePic } : require('../../assets/AddKidPic.png')}
                      style={styles.ticketGuestAvatar}
                    />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.ticketGuestName} numberOfLines={1}>
                        {kid.name}
                      </Text>
                      <Text style={styles.ticketGuestMeta}>
                        {kid.age} yrs · {kid.gender === 'BOY' ? 'Boy' : 'Girl'}
                      </Text>
                    </View>
                    {kid.checkedIn && (
                      <View style={styles.checkedInPill}>
                        <BadgeCheckIcon size={13} color={GREEN_INK} strokeWidth={2.2} />
                        <Text style={styles.checkedInPillText}>Checked in</Text>
                      </View>
                    )}
                  </View>
                ))}
              </View>
            </View>
          );
        })}
      </View>

      {/* Footer — status + pay + download */}
      <View style={styles.ticketFooter}>
        <View style={styles.ticketFooterMeta}>
          <View style={[styles.paymentPill, { backgroundColor: pill.bg }]}>
            <Text style={[styles.paymentPillText, { color: pill.fg }]}>{pill.label}</Text>
          </View>
          <Text style={styles.ticketIdText} numberOfLines={1}>
            {registration.registrationId}
          </Text>
        </View>

        <View style={[styles.ticketFooterActions, !isWide && styles.ticketFooterActionsMobile]}>
          {isInvoiceDue && (
            <TouchableOpacity
              activeOpacity={0.85}
              style={[styles.payButton, !isWide && styles.footerButtonMobileHalf]}
              onPress={() => onPayInvoice(invoice.invoiceId)}
            >
              <Text style={styles.payButtonText}>Pay</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            activeOpacity={0.85}
            style={[
              styles.downloadQrButton,
              downloading && styles.downloadQrButtonDisabled,
              !isWide && styles.footerButtonMobileFull,
            ]}
            onPress={() => onDownloadQr(registration)}
            disabled={downloading}
          >
            {downloading ? (
              <ActivityIndicator size="small" color={GREEN_INK} />
            ) : (
              <>
                <DownloadIcon size={16} color={GREEN_INK} strokeWidth={2} />
                <Text style={styles.downloadQrButtonText}>Download QR</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>

      {/* Follow-up booking for this same ticket/date */}
      <View style={styles.newRegistrationWrap}>
        <Pressable
          style={({ hovered, pressed }) => [
            styles.newRegistrationButton,
            hovered && styles.newRegistrationButtonHovered,
            pressed && styles.newRegistrationButtonPressed,
          ]}
          onPress={() => onNewRegistration(registration)}
        >
          <View style={styles.newRegistrationTextWrap}>
            <Text style={styles.newRegistrationTitle}>+ New Registration</Text>
            <Text style={styles.newRegistrationSubtitle}>
              {addsToThisInvoice
                ? `Book tickets for this session · added to invoice ${invoice.invoiceId || 'above'}`
                : 'Book tickets for this session · billed on a new invoice'}
            </Text>
          </View>
          <ChevronRightIcon size={20} color="#fff" strokeWidth={2.4} />
        </Pressable>
      </View>
    </View>
  );
};

const DateRegistrationSection = ({
  summary,
  isWide,
  collapsed,
  onToggle,
  onAddKid,
  onDownloadQr,
  onPayInvoice,
  onNewRegistration,
  qrDownloadingId,
}) => {
  const { date, index, registrations, kidCount, state, anchored } = summary;
  const isPast = state === 'past';
  const stateBadge = DAY_STATE_BADGE[state] || null;
  const tickets = date.tickets || [];
  const seatsLeft = tickets.reduce((sum, t) => sum + (t.remainingSeats ?? 0), 0);
  const totalSeats = tickets.reduce((sum, t) => sum + (t.totalSeats ?? 0), 0);
  const soldOut = totalSeats > 0 && seatsLeft <= 0;
  const lowSeats = !soldOut && totalSeats > 0 && seatsLeft / totalSeats <= 0.15;
  const cheapest = tickets.reduce(
    (best, t) => (best == null || (Number(t.price) || 0) < (Number(best.price) || 0) ? t : best),
    null,
  );
  const kidsPerTicket = cheapest?.no_of_kids ?? 1;
  const hasRegistrations = registrations.length > 0;
  const canAdd = !soldOut && tickets.length > 0;

  const pill = hasRegistrations
    ? { bg: '#EAF3DC', fg: GREEN_INK, label: 'REGISTERED' }
    : soldOut
    ? { bg: '#FBE3E2', fg: DANGER, label: 'SOLD OUT' }
    : { bg: CHIP_BG, fg: '#5A5C7A', label: 'OPEN' };

  return (
    <View
      style={[
        styles.dateSection,
        isPast && styles.dateSectionPast,
        anchored && (state === 'today' ? styles.dateSectionToday : styles.dateSectionNext),
      ]}
    >
      <Pressable
        style={({ hovered }) => [styles.dateSectionHeader, hovered && styles.dateSectionHeaderHovered]}
        onPress={() => onToggle(date.eventDateId)}
      >
        <View
          style={[
            styles.dayChip,
            isPast && styles.dayChipPast,
            anchored && (state === 'today' ? styles.dayChipToday : styles.dayChipNext),
          ]}
        >
          <Text
            style={[
              styles.dayChipWeekday,
              isPast && !anchored && styles.dayChipTextPast,
              anchored && state === 'today' && styles.dayChipTextToday,
            ]}
          >
            {formatWeekdayChip(date.eventDate)}
          </Text>
          <Text
            style={[
              styles.dayChipNumber,
              isPast && !anchored && styles.dayChipTextPast,
              anchored && state === 'today' && styles.dayChipTextToday,
            ]}
          >
            {formatDayNumber(date.eventDate)}
          </Text>
        </View>

        <View style={styles.dateSectionHeaderMain}>
          <View style={styles.dateSectionTitleRow}>
            <Text style={styles.dateSectionTitle}>{formatDateCompact(date.eventDate)}</Text>
            {stateBadge ? (
              <View style={[styles.dayStatusPill, { backgroundColor: stateBadge.bg }]}>
                <Text style={[styles.dayStatusPillText, { color: stateBadge.fg }]}>{stateBadge.label}</Text>
              </View>
            ) : null}
            <View style={[styles.dayStatusPill, { backgroundColor: pill.bg }]}>
              <Text style={[styles.dayStatusPillText, { color: pill.fg }]}>{pill.label}</Text>
            </View>
          </View>

          <Text style={styles.dateSectionSubtitle}>
            Day {index + 1}
            {date.startTime ? ` · ${date.startTime} - ${date.endTime}` : ''}
          </Text>

          <View style={styles.dateSectionMetaRow}>
            <View style={styles.dateSectionMetaItem}>
              <View
                style={[styles.seatDot, soldOut ? styles.seatDotDanger : lowSeats ? styles.seatDotWarn : null]}
              />
              <Text style={styles.dateSectionMetaText}>
                {totalSeats === 0
                  ? 'No tickets published'
                  : soldOut
                  ? 'No seats left'
                  : `${seatsLeft} of ${totalSeats} seats left`}
              </Text>
            </View>

            {cheapest ? (
              <Text style={styles.dateSectionMetaPrice}>
                From ${cheapest.price} / ticket ({kidsPerTicket} {kidsPerTicket === 1 ? 'kid' : 'kids'})
              </Text>
            ) : null}

            {collapsed && hasRegistrations ? (
              <Text style={styles.dateSectionMetaText}>{kidCount} attending</Text>
            ) : null}
          </View>
        </View>

        <View style={[styles.dateSectionChevron, !collapsed && styles.dateSectionChevronOpen]}>
          <ChevronDownIcon size={18} color={MUTED} strokeWidth={2.2} />
        </View>
      </Pressable>

      {collapsed ? null : (
      <Text style={styles.participantsLabel}>
        {hasRegistrations
          ? `REGISTERED PARTICIPANTS FOR ${formatDateMedium(date.eventDate).toUpperCase()} (${kidCount} ${
              kidCount === 1 ? 'KID' : 'KIDS'
            })`
          : `NO PARTICIPANTS FOR ${formatDateMedium(date.eventDate).toUpperCase()} YET`}
      </Text>

      )}

      {collapsed ? null : hasRegistrations ? (
        registrations.map((registration) => (
          <RegistrationCard
            key={registration.registrationId}
            registration={registration}
            isWide={isWide}
            onDownloadQr={onDownloadQr}
            onPayInvoice={onPayInvoice}
            onNewRegistration={onNewRegistration}
            downloading={qrDownloadingId === registration.registrationId}
          />
        ))
      ) : (
        <Pressable
          style={({ hovered }) => [styles.emptyDateBox, hovered && canAdd && styles.emptyDateBoxHovered]}
          onPress={() => canAdd && onAddKid(date)}
        >
          <Text style={styles.emptyDateBoxText}>
            {canAdd ? `+ Add a child for ${formatDateMedium(date.eventDate)}` : 'This day is fully booked.'}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

const ProfileEventDetails = ({ navigation }) => {
  const route = useRoute();
  const { event: initialEvent } = route.params || {};

  const [isWide, setIsWide] = useState(Dimensions.get('window').width >= WIDE_BREAKPOINT);
  const [event, setEvent] = useState(initialEvent || null);
  const [loading, setLoading] = useState(!!initialEvent?.eventId);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [addTargetDate, setAddTargetDate] = useState(null);
  const [editRegistration, setEditRegistration] = useState(null);
  const [expandedDateId, setExpandedDateId] = useState(null);
  // Holds only the dates the user has closed, so every date starts expanded.
  const [collapsedDates, setCollapsedDates] = useState([]);
  const [qrDownloadingId, setQrDownloadingId] = useState(null);
  const [qrJob, setQrJob] = useState(null);
  const qrRef = useRef(null);
  const windowWidth = Dimensions.get('window').width;

  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => {
      setIsWide(window.width >= WIDE_BREAKPOINT);
    });
    return () => sub?.remove && sub.remove();
  }, []);

  const fetchEventDetails = useCallback(async (eventId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_PATHS.BASE_URL}${API_PATHS.GET_EVENT_DETAILS}/${eventId}`);
      if (response.data.Success) {
        setEvent((prev) => ({ ...prev, ...response.data.Data }));
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialEvent?.eventId) {
      fetchEventDetails(initialEvent.eventId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadQrPng = useCallback((base64, ticketCode) => {
    const fileName = `ticket-${ticketCode || 'event'}-qr.png`;
    if (Platform.OS === 'web') {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${base64}`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    RNFS.writeFile(`${RNFS.DocumentDirectoryPath}/${fileName}`, base64, 'base64')
      .then(() => Toast.show({ type: 'success', text1: 'QR code downloaded.' }))
      .catch((error) => {
        console.error('Error saving QR code:', error);
        Toast.show({ type: 'error', text1: 'Could not download QR code.' });
      });
  }, []);

  const toggleDateSection = useCallback((eventDateId) => {
    setCollapsedDates((prev) =>
      prev.includes(eventDateId) ? prev.filter((id) => id !== eventDateId) : [...prev, eventDateId],
    );
  }, []);

  const handleAddKid = useCallback((date) => {
    setEditRegistration(null);
    setAddTargetDate(date);
    setShowAddParticipant(true);
  }, []);

  const handleNewRegistration = useCallback(
    (registration) => {
      const dateId = registration?.eventDate?.eventDateId;
      const date = (event?.dates || []).find((d) => d.eventDateId === dateId) || registration?.eventDate || null;
      // Handing the modal an editRegistration is what routes the final submit to
      // edit-event-registration instead of register-for-event.
      setEditRegistration(isRegistrationUnpaid(registration) ? registration : null);
      setAddTargetDate(date);
      setShowAddParticipant(true);
    },
    [event],
  );

  const handlePayInvoice = useCallback((invoiceId) => {
    if (!invoiceId) return;
    const url = `https://freedommerchants.com/danileeshhmulti.php?inum=${encodeURIComponent(invoiceId)}`;
    if (Platform.OS === 'web') {
      window.open(url, '_blank');
      return;
    }
    Linking.openURL(url).catch((error) => {
      console.error('Error opening payment link:', error);
      Toast.show({ type: 'error', text1: 'Could not open the payment page.' });
    });
  }, []);

  const handleDownloadRegistrationQr = useCallback(
    async (registration) => {
      const registrationId = registration?.registrationId;
      if (qrDownloadingId || !registrationId) return;
      setQrDownloadingId(registrationId);

      const existingToken = registration?.qr?.qrToken;
      if (existingToken) {
        setQrJob({ token: existingToken, ticketCode: registrationId });
        return;
      }
      try {
        const response = await axios.post(`${API_PATHS.BASE_URL}${API_PATHS.GENERATE_EVENT_TICKET_QR}`, {
          registrationId,
        });
        const token = response.data?.Data?.qrToken;
        if ((response.data?.Success === 1 || response.data?.Success === true) && token) {
          setQrJob({ token, ticketCode: registrationId });
        } else {
          Toast.show({ type: 'error', text1: response.data?.Message || 'Could not generate the QR code.' });
          setQrDownloadingId(null);
        }
      } catch (error) {
        console.error('Error generating event ticket QR:', error);
        Toast.show({
          type: 'error',
          text1: error?.response?.data?.Message || 'Could not generate the QR code.',
        });
        setQrDownloadingId(null);
      }
    },
    [qrDownloadingId],
  );

  useEffect(() => {
    if (!qrJob?.token) return;
    const timer = setTimeout(() => {
      if (qrRef.current?.toDataURL) {
        qrRef.current.toDataURL((base64) => downloadQrPng(base64, qrJob.ticketCode));
      }
      setQrJob(null);
      setQrDownloadingId(null);
    }, 0);
    return () => clearTimeout(timer);
  }, [qrJob, downloadQrPng]);

  if (!event) {
    return (
      <View style={styles.container}>
        <TopGreenHeader Heading="Details" navigation={navigation} isBackIcon={true} isNotification={false} />
        <View style={styles.loadingContainer}>
          <Text style={styles.emptyText}>Event not found.</Text>
        </View>
      </View>
    );
  }

  const eventDates = [...(event.dates || [])].sort((a, b) => (a.eventDate || 0) - (b.eventDate || 0));
  const bannerSource = event.pic_url?.[0] ? { uri: event.pic_url[0] } : require('../../assets/event-banner.png');
  const allRegistrations = event.myRegistrations || [];

  const registrationsByDateId = allRegistrations.reduce((acc, reg) => {
    const dateId = reg?.eventDate?.eventDateId;
    if (dateId == null) return acc;
    (acc[dateId] = acc[dateId] || []).push(reg);
    return acc;
  }, {});

  const today = todayMarker();
  const baseSummaries = eventDates.map((date, index) => {
    const dateRegistrations = registrationsByDateId[date.eventDateId] || [];
    const eventDay = date.eventDate;
    return {
      date,
      index,
      registrations: dateRegistrations,
      kids: collectKids(dateRegistrations),
      kidCount: collectKids(dateRegistrations).length,
      amount: dateRegistrations.reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0),
      state: eventDay == null ? null : eventDay < today ? 'past' : eventDay === today ? 'today' : 'upcoming',
    };
  });

  // Only the soonest still-to-come day is called out, so the highlight stays unique.
  const nextIndex = baseSummaries.findIndex((s) => s.state === 'upcoming');
  const todayIndex = baseSummaries.findIndex((s) => s.state === 'today');
  const anchorIndex =
    todayIndex >= 0 ? todayIndex : nextIndex >= 0 ? nextIndex : baseSummaries.length ? 0 : -1;

  const dateSummaries = baseSummaries.map((s, i) => ({
    ...s,
    state: i === nextIndex ? 'next' : s.state,
    anchored: i === anchorIndex,
  }));
  const highlighted = anchorIndex >= 0 ? dateSummaries[anchorIndex] : null;
  const allPast = dateSummaries.length > 0 && dateSummaries.every((s) => s.state === 'past');

  const uniqueKids = collectKids(allRegistrations);
  // A child on both days fills two day-places but is still one enrolled child,
  // which is why this total is larger than uniqueKids.length.
  const totalDayPlaces = dateSummaries.reduce((sum, s) => sum + s.kidCount, 0);

  // A registration pointing at a date the event no longer lists would otherwise
  // be counted in the total but shown in no row, so it gets a row of its own.
  const listedDateIds = new Set(eventDates.map((d) => d.eventDateId));
  const orphanRegistrations = allRegistrations.filter(
    (r) => !listedDateIds.has(r?.eventDate?.eventDateId),
  );
  const orphanSummary = orphanRegistrations.length
    ? {
        kidCount: collectKids(orphanRegistrations).length,
        amount: orphanRegistrations.reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0),
      }
    : null;

  // Subtotal is what the rows above add up to — the gross price of every
  // registration. Total Due is what is still outstanding on the invoices, which
  // is a different figure the moment anything is paid.
  const subtotal =
    dateSummaries.reduce((sum, s) => sum + s.amount, 0) + (orphanSummary?.amount || 0);

  const countedInvoices = new Set();
  const amountDue = allRegistrations.reduce((sum, reg) => {
    const status = String(reg.invoice?.status || reg.paymentStatus || '').toUpperCase();
    if (['PAID', 'COMPLETED', 'SUCCESS', 'CANCELLED'].includes(status)) return sum;
    const invoiceId = reg.invoice?.invoiceId;
    if (invoiceId) {
      if (countedInvoices.has(invoiceId)) return sum;
      countedInvoices.add(invoiceId);
    }
    return sum + (Number(reg.invoice?.balance ?? reg.totalAmount) || 0);
  }, 0);

  const amountPaid = Math.max(subtotal - amountDue, 0);

  const firstDate = eventDates[0]?.eventDate;
  const lastDate = eventDates[eventDates.length - 1]?.eventDate;
  const campDatesLabel =
    eventDates.length === 0
      ? '—'
      : eventDates.length === 1
      ? formatDateCompact(firstDate)
      : `${formatDateMedium(firstDate)} – ${formatDateMedium(lastDate)}, ${toDate(lastDate).getUTCFullYear()}`;

  const eventDetailsCard = (
    <View style={[styles.card, cardShadow, !isWide && styles.cardMobile]}>
      <View style={styles.cardHeaderRow}>
        <InfoIcon size={22} color={GREEN_INK} strokeWidth={2} />
        <Text style={styles.cardHeaderText}>Event Details</Text>
      </View>

      {loading ? (
        <ActivityIndicator size="small" color={GREEN_INK} />
      ) : event.details ? (
        <RenderHtml
          contentWidth={windowWidth - (isWide ? 88 : 68)}
          source={{ html: event.details }}
          systemFonts={RENDER_HTML_SYSTEM_FONTS}
          baseStyle={{ fontFamily: FONT, fontSize: 15, lineHeight: 23, color: '#4A4A55' }}
          tagsStyles={{
            p: { marginBottom: 8, fontSize: 15, lineHeight: 23, color: '#4A4A55', fontFamily: FONT },
            ul: { marginLeft: 0, marginBottom: 8, paddingLeft: 20 },
            li: { marginBottom: 4, fontSize: 15, lineHeight: 23, color: '#4A4A55', fontFamily: FONT },
            strong: { fontWeight: 'bold', color: INK, fontFamily: FONT_BOLD },
          }}
        />
      ) : (
        <Text style={styles.paragraph}>No additional details available for this event.</Text>
      )}
    </View>
  );

  const dateWiseCard = (
    <View style={[styles.card, cardShadow, styles.kidsCard, !isWide && styles.cardMobile]}>
      <View style={styles.cardHeaderRow}>
        <CalendarIcon size={22} color={GREEN_INK} strokeWidth={2} />
        <Text style={styles.cardHeaderText}>Date-Wise Kids Registration</Text>
      </View>
      <Text style={styles.cardHeaderSubtext}>
        Register your children for every date, or pick only the days that work for you.
      </Text>

      {loading ? (
        <ActivityIndicator size="small" color={GREEN_INK} />
      ) : dateSummaries.length === 0 ? (
        <Text style={styles.registrationsEmptyText}>No dates have been scheduled for this event yet.</Text>
      ) : (
        dateSummaries.map((summary) => (
          <DateRegistrationSection
            key={summary.date.eventDateId}
            summary={summary}
            isWide={isWide}
            collapsed={collapsedDates.includes(summary.date.eventDateId)}
            onToggle={toggleDateSection}
            onAddKid={handleAddKid}
            onDownloadQr={handleDownloadRegistrationQr}
            onPayInvoice={handlePayInvoice}
            onNewRegistration={handleNewRegistration}
            qrDownloadingId={qrDownloadingId}
          />
        ))
      )}
    </View>
  );

  const summaryCard = (
    <View style={[styles.card, cardShadow, isWide && styles.sessionCardWide, !isWide && styles.cardMobile]}>
      <Text style={styles.sessionInfoHeading}>REGISTRATION SUMMARY</Text>

      <SessionRow
        icon={<CalendarIcon size={20} color={GREEN_INK} strokeWidth={2} />}
        label="EVENT DATES"
        value={campDatesLabel}
        extra={
          <Text style={styles.summaryHint}>
            {eventDates.length} {eventDates.length === 1 ? 'day' : 'days'} scheduled
          </Text>
        }
      />

      <SessionRow
        icon={<ClockIcon size={20} color={GREEN_INK} strokeWidth={2} />}
        label={highlighted?.state === 'today' ? 'TODAY' : allPast ? 'EVENT STARTED' : 'NEXT SESSION'}
        value={
          highlighted
            ? `Day ${highlighted.index + 1} · ${formatDateCompact(highlighted.date.eventDate)}`
            : 'All dates have passed'
        }
        extra={
          highlighted ? (
            <Text style={styles.summaryHint}>
              {highlighted.date.startTime} - {highlighted.date.endTime}
            </Text>
          ) : null
        }
      />

      <SessionRow
        icon={<BadgeCheckIcon size={20} color={GREEN_INK} strokeWidth={2} />}
        label="ENROLLED KIDS"
        value={
          uniqueKids.length === 0
            ? 'None yet'
            : `${uniqueKids.length} ${uniqueKids.length === 1 ? 'child' : 'children'}`
        }
        extra={
          uniqueKids.length ? (
            <>
              <Text style={styles.summaryHint}>
                {`Attending ${totalDayPlaces} day-${totalDayPlaces === 1 ? 'session' : 'sessions'} in total`}
              </Text>
              <Text style={styles.summaryHint} numberOfLines={3}>
                {uniqueKids.map((kid) => (kid.name || '').trim()).join(' • ')}
              </Text>
            </>
          ) : null
        }
      />

      <View style={[styles.infoRow, styles.infoRowLast]}>
        <View style={styles.infoIconChip}>
          <TicketIcon size={20} color={GREEN_INK} strokeWidth={2} />
        </View>
        <View style={styles.seatsColumn}>
          <Text style={styles.infoLabel}>DATES REGISTERED</Text>

          {dateSummaries.length === 0 ? (
            <Text style={styles.summaryHint}>—</Text>
          ) : (
            dateSummaries.map((summary) => {
              const empty = summary.kidCount === 0;
              const open = expandedDateId === summary.date.eventDateId;
              return (
                <View key={summary.date.eventDateId} style={styles.summaryDateGroup}>
                  <Pressable
                    style={({ hovered }) => [styles.summaryDateRow, hovered && styles.summaryDateRowHovered]}
                    onPress={() =>
                      setExpandedDateId(open ? null : summary.date.eventDateId)
                    }
                    disabled={empty}
                  >
                    <View style={[styles.summaryChevron, open && styles.summaryChevronOpen]}>
                      <ChevronDownIcon size={16} color={empty ? '#C8C8D2' : MUTED} strokeWidth={2.2} />
                    </View>

                    <View style={styles.summaryDateTextWrap}>
                      <Text style={[styles.summaryDateLabel, empty && styles.summaryDateMuted]} numberOfLines={1}>
                        {formatDateCompact(summary.date.eventDate)}
                      </Text>
                      <Text style={[styles.summaryDateCount, empty && styles.summaryDateMuted]}>
                        {empty ? 'Nobody attending' : `${summary.kidCount} attending`}
                      </Text>
                    </View>

                    <Text style={[styles.summaryDateAmount, empty && styles.summaryDateMuted]}>
                      {empty ? '—' : formatMoney(summary.amount)}
                    </Text>
                  </Pressable>

                  {open && !empty ? (
                    <View style={styles.summaryKidList}>
                      {summary.kids.map((kid) => (
                        <View key={kid.kidId} style={styles.summaryKidRow}>
                          <Image
                            source={kid.profilePic ? { uri: kid.profilePic } : require('../../assets/AddKidPic.png')}
                            style={styles.summaryKidAvatar}
                          />
                          <View style={styles.summaryKidTextWrap}>
                            <Text style={styles.summaryKidName} numberOfLines={1}>
                              {(kid.name || '').trim()}
                            </Text>
                            <Text style={styles.summaryKidMeta}>
                              {kid.age} yrs · {kid.gender === 'BOY' ? 'Boy' : 'Girl'}
                            </Text>
                          </View>
                          {kid.checkedIn ? (
                            <View style={styles.summaryCheckedInPill}>
                              <BadgeCheckIcon size={12} color={GREEN_INK} strokeWidth={2.2} />
                              <Text style={styles.summaryCheckedInText}>In</Text>
                            </View>
                          ) : null}
                        </View>
                      ))}

                      <Text style={styles.summaryKidFootnote}>
                        {summary.registrations.length}{' '}
                        {summary.registrations.length === 1 ? 'registration' : 'registrations'} on this date
                      </Text>
                    </View>
                  ) : null}
                </View>
              );
            })
          )}

          {orphanSummary ? (
            <View style={styles.summaryDateGroup}>
              <View style={styles.summaryDateRow}>
                <View style={styles.summaryChevron} />
                <View style={styles.summaryDateTextWrap}>
                  <Text style={styles.summaryDateLabel} numberOfLines={1}>
                    Other dates
                  </Text>
                  <Text style={styles.summaryDateCount}>{orphanSummary.kidCount} attending</Text>
                </View>
                <Text style={styles.summaryDateAmount}>{formatMoney(orphanSummary.amount)}</Text>
              </View>
            </View>
          ) : null}

          <View style={styles.summaryDivider} />

          <View style={styles.summarySubRow}>
            <Text style={styles.summarySubLabel}>Subtotal</Text>
            <Text style={styles.summarySubValue}>{formatMoney(subtotal)}</Text>
          </View>

          {amountPaid > 0 ? (
            <View style={styles.summarySubRow}>
              <Text style={styles.summarySubLabel}>Paid</Text>
              <Text style={[styles.summarySubValue, styles.summaryPaidValue]}>
                −{formatMoney(amountPaid)}
              </Text>
            </View>
          ) : null}

          <View style={styles.summaryTotalRow}>
            <Text style={styles.summaryTotalLabel}>Total Due</Text>
            <Text style={styles.summaryTotalValue}>{formatMoney(amountDue)}</Text>
          </View>
        </View>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <TopGreenHeader Heading="Details" navigation={navigation} isBackIcon={true} isNotification={false} />

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.pageInner, isWide && styles.pageInnerWide, !isWide && styles.pageInnerMobile]}>
          <Image source={bannerSource} style={[styles.bannerImage, !isWide && styles.bannerImageMobile]} resizeMode="cover" />

          <View style={[styles.headerRow, !isWide && styles.headerRowStacked]}>
            <View style={styles.headerLeft}>
              <Text style={[styles.title, !isWide && styles.titleMobile]}>{event.title}</Text>
            </View>

            <View
              style={[
                styles.statusBadge,
                badgeShadow,
                !isWide && styles.statusBadgeStacked,
                { backgroundColor: STATUS_COLORS[event.status] || GREEN },
              ]}
            >
              <Text style={styles.statusText}>{(event.status || '').toUpperCase()}</Text>
            </View>
          </View>

          <View style={[styles.divider, !isWide && styles.dividerMobile]} />

          {isWide ? (
            <View style={[styles.cardsRow, styles.cardsRowWide]}>
              <View style={[styles.leftColumn, styles.leftColumnWide]}>
                {eventDetailsCard}
                {dateWiseCard}
              </View>
              {summaryCard}
            </View>
          ) : (
            <View style={styles.cardsRow}>
              {summaryCard}
              {eventDetailsCard}
              {dateWiseCard}
            </View>
          )}
        </View>
      </ScrollView>

      {qrJob?.token ? (
        <View style={styles.hiddenQr} pointerEvents="none">
          <QrCodeView ref={qrRef} value={qrJob.token} size={200} />
        </View>
      ) : null}

      <AddParticipantModal
        visible={showAddParticipant}
        onClose={(bookingCompleted) => {
          setShowAddParticipant(false);
          setAddTargetDate(null);
          setEditRegistration(null);
          if (bookingCompleted) {
            fetchEventDetails(event.eventId);
          }
        }}
        tickets={addTargetDate?.tickets || []}
        eventId={event.eventId}
        eventTitle={event.title}
        eventDate={addTargetDate}
        editRegistration={editRegistration}
      />
    </View>
  );
};

const FONT = Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans';
const FONT_BOLD = Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans';

const RENDER_HTML_SYSTEM_FONTS = [...defaultSystemFonts, 'Facebook Sans', 'FacebookSansRegular', 'FacebookSansBold'];

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    backgroundColor: '#F8F8FC',
    flexGrow: 1,
    paddingBottom: 40,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  emptyText: {
    textAlign: 'center',
    color: MUTED,
    fontSize: 15,
    fontFamily: FONT,
  },
  pageInner: {
    width: '100%',
    maxWidth: 1080,
    alignSelf: 'center',
    paddingHorizontal: 20,
    paddingTop: 0,
  },
  pageInnerWide: {
    width: '80%',
    maxWidth: 1400,
  },
  pageInnerMobile: {
    paddingHorizontal: 14,
  },
  bannerImage: {
    width: '100%',
    height: 240,
    marginTop: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    backgroundColor: '#E7E7EE',
  },
  bannerImageMobile: {
    height: 170,
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 28,
  },
  headerRowStacked: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flexShrink: 1,
  },
  title: {
    fontSize: 30,
    color: INK,
    letterSpacing: -0.4,
    fontFamily: FONT_BOLD,
  },
  titleMobile: {
    fontSize: 22,
  },
  statusBadge: {
    backgroundColor: GREEN,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 999,
  },
  statusText: {
    color: '#fff',
    fontSize: 11.5,
    letterSpacing: 0.6,
    fontFamily: FONT_BOLD,
  },
  statusBadgeStacked: {
    marginTop: 16,
    alignSelf: 'flex-start',
  },
  subtitle: {
    fontSize: 16,
    color: MUTED,
    marginTop: 8,
    fontFamily: FONT,
  },
  divider: {
    height: 1,
    backgroundColor: HAIRLINE,
    marginTop: 28,
    marginBottom: 28,
  },
  dividerMobile: {
    marginTop: 18,
    marginBottom: 18,
  },
  cardsRow: {
    flexDirection: 'column',
  },
  cardsRowWide: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  leftColumn: {
    width: '100%',
  },
  leftColumnWide: {
    flex: 2,
    marginRight: 20,
  },
  card: {
    borderWidth: 1,
    borderColor: HAIRLINE,
    borderRadius: 16,
    padding: 24,
    marginBottom: 20,
    backgroundColor: '#fff',
  },
  cardMobile: {
    padding: 18,
    marginBottom: 16,
  },
  sessionCardWide: {
    flex: 1,
    marginBottom: 0,
  },
  kidsCard: {
    marginBottom: 0,
  },
  registrationsEmptyText: {
    fontSize: 14,
    color: MUTED,
    fontFamily: FONT,
    marginBottom: 4,
  },

  // ---- Ticket card ----
  ticketCardOuter: {
    position: 'relative',
    backgroundColor: '#FBFCFA',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: HAIRLINE,
    marginBottom: 16,
    overflow: 'visible',
    ...cardShadow,
  },
  ticketHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 14,
  },
  ticketHeadLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
  },
  ticketHeadIconChip: {
    width: 38,
    height: 38,
    borderRadius: 11,
    backgroundColor: '#EDF3E2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ticketHeadText: {
    flex: 1,
  },
  ticketTitleText: {
    fontSize: 15.5,
    color: INK,
    letterSpacing: -0.1,
    fontFamily: FONT_BOLD,
  },
  ticketTypeText: {
    fontSize: 12,
    color: MUTED,
    marginTop: 3,
    letterSpacing: 0.2,
    fontFamily: FONT,
  },
  ticketPriceBlock: {
    alignItems: 'center',
    alignSelf: 'center',
  },
  ticketPriceValue: {
    fontSize: 18,
    color: INK,
    letterSpacing: -0.3,
    fontFamily: FONT_BOLD,
  },
  ticketPerfWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 18,
  },
  ticketPerfLine: {
    flex: 1,
    borderTopWidth: 1.5,
    borderColor: '#D6DBCF',
    borderStyle: 'dashed',
    marginHorizontal: 12,
  },
  ticketNotch: {
    position: 'absolute',
    top: 1,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: HAIRLINE,
  },
  ticketNotchLeft: {
    left: -8,
  },
  ticketNotchRight: {
    right: -8,
  },
  ticketBody: {
    paddingHorizontal: 14,
    paddingTop: 6,
    paddingBottom: 14,
  },
  regTicketBlock: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: HAIRLINE,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 13,
    marginTop: 10,
  },
  regTicketTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  regTicketTitle: {
    flex: 1,
    fontSize: 14.5,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  regTicketPrice: {
    fontSize: 14,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  ticketGuestList: {
    marginTop: 12,
    gap: 12,
  },
  ticketGuestRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  ticketGuestAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: CHIP_BG,
    borderWidth: 1,
    borderColor: HAIRLINE,
  },
  ticketGuestName: {
    fontSize: 14,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  ticketGuestMeta: {
    fontSize: 12,
    color: MUTED,
    marginTop: 2,
    fontFamily: FONT,
  },
  checkedInPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#EAF3DC',
    borderRadius: 999,
    paddingHorizontal: 9,
    paddingVertical: 4,
  },
  checkedInPillText: {
    fontSize: 10.5,
    color: GREEN_INK,
    fontFamily: FONT_BOLD,
  },
  ticketFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: HAIRLINE,
    backgroundColor: '#F6F8F2',
  },
  ticketFooterMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
  },
  paymentPill: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  paymentPillText: {
    fontSize: 10,
    letterSpacing: 0.6,
    fontFamily: FONT_BOLD,
  },
  ticketIdText: {
    fontSize: 11.5,
    color: MUTED,
    flexShrink: 1,
    fontFamily: FONT,
  },
  ticketFooterActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  ticketFooterActionsMobile: {
    width: '100%',
    flexWrap: 'wrap',
  },
  footerButtonMobileHalf: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  footerButtonMobileFull: {
    flexBasis: '100%',
    minWidth: 0,
  },
  payButton: {
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 88,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: GREEN,
  },
  payButtonText: {
    fontSize: 13,
    color: '#fff',
    letterSpacing: 0.2,
    fontFamily: FONT_BOLD,
  },
  downloadQrButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    minWidth: 132,
    borderWidth: 1.5,
    borderColor: GREEN,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 9,
    backgroundColor: '#fff',
  },
  downloadQrButtonDisabled: {
    opacity: 0.55,
  },
  downloadQrButtonText: {
    fontSize: 13,
    color: GREEN_INK,
    letterSpacing: 0.2,
    fontFamily: FONT_BOLD,
  },
  hiddenQr: {
    position: 'absolute',
    width: 0,
    height: 0,
    overflow: 'hidden',
    opacity: 0,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    marginBottom: 18,
  },
  cardHeaderText: {
    fontSize: 20,
    color: INK,
    letterSpacing: -0.2,
    fontFamily: FONT_BOLD,
  },
  paragraph: {
    fontSize: 15,
    lineHeight: 23,
    color: '#4A4A55',
    marginBottom: 14,
    fontFamily: FONT,
  },
  bold: {
    fontFamily: FONT_BOLD,
    color: INK,
  },
  sessionInfoHeading: {
    fontSize: 12.5,
    letterSpacing: 0.8,
    color: MUTED,
    marginBottom: 20,
    fontFamily: FONT_BOLD,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 22,
  },
  infoRowLast: {
    marginBottom: 0,
  },
  infoIconChip: {
    width: 40,
    height: 40,
    borderRadius: 11,
    backgroundColor: CHIP_BG,
    borderWidth: 1,
    borderColor: CHIP_BORDER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoLabel: {
    fontSize: 11.5,
    letterSpacing: 0.5,
    color: MUTED,
    fontFamily: FONT_BOLD,
  },
  infoValue: {
    fontSize: 17,
    color: INK,
    marginTop: 4,
    fontFamily: FONT_BOLD,
  },
  seatsColumn: {
    flex: 1,
  },

  cardHeaderSubtext: {
    fontSize: 13.5,
    lineHeight: 20,
    color: MUTED,
    fontFamily: FONT,
    marginTop: -6,
    marginBottom: 18,
  },
  dateSection: {
    borderWidth: 1,
    borderColor: HAIRLINE,
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    backgroundColor: '#FCFCFE',
  },
  dateSectionHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    borderRadius: 10,
    marginHorizontal: -6,
    marginTop: -4,
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
  dateSectionHeaderHovered: {
    backgroundColor: 'rgba(23,23,32,0.03)',
  },
  dateSectionChevron: {
    paddingTop: 6,
  },
  dateSectionChevronOpen: {
    transform: [{ rotate: '180deg' }],
  },
  dayChip: {
    width: 52,
    paddingVertical: 8,
    borderRadius: 12,
    alignItems: 'center',
    backgroundColor: CHIP_BG,
    borderWidth: 1,
    borderColor: CHIP_BORDER,
  },
  dayChipWeekday: {
    fontSize: 10.5,
    letterSpacing: 0.8,
    color: '#5A5C7A',
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  dayChipNumber: {
    fontSize: 20,
    lineHeight: 26,
    color: INK,
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  dateSectionHeaderMain: {
    flex: 1,
    minWidth: 0,
  },
  dateSectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  dateSectionTitle: {
    fontSize: 16.5,
    color: INK,
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  dayStatusPill: {
    paddingHorizontal: 9,
    paddingVertical: 3,
    borderRadius: 999,
  },
  dayStatusPillText: {
    fontSize: 10,
    letterSpacing: 0.6,
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  dateSectionSubtitle: {
    marginTop: 3,
    fontSize: 13.5,
    lineHeight: 20,
    color: '#4A4A55',
    fontFamily: FONT,
  },
  dateSectionMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 14,
    marginTop: 8,
  },
  dateSectionMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  seatDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: GREEN,
  },
  seatDotWarn: {
    backgroundColor: AMBER,
  },
  seatDotDanger: {
    backgroundColor: DANGER,
  },
  dateSectionMetaText: {
    fontSize: 12.5,
    color: MUTED,
    fontFamily: FONT,
  },
  dateSectionMetaPrice: {
    fontSize: 12.5,
    color: INK,
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  participantsLabel: {
    marginTop: 16,
    marginBottom: 10,
    fontSize: 10.5,
    letterSpacing: 0.8,
    color: MUTED,
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  emptyDateBox: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: CHIP_BORDER,
    borderRadius: 12,
    paddingVertical: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyDateBoxHovered: {
    borderColor: GREEN,
    backgroundColor: '#F6FAEF',
  },
  emptyDateBoxText: {
    fontSize: 13,
    color: MUTED,
    fontFamily: FONT,
  },
  summaryHint: {
    marginTop: 3,
    fontSize: 12.5,
    lineHeight: 18,
    color: MUTED,
    fontFamily: FONT,
  },
  summaryDateGroup: {
    marginTop: 6,
  },
  summaryDateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 8,
    marginHorizontal: -8,
    borderRadius: 10,
  },
  summaryDateRowHovered: {
    backgroundColor: '#F4F6FB',
  },
  summaryChevron: {
    width: 18,
    alignItems: 'center',
  },
  summaryChevronOpen: {
    transform: [{ rotate: '180deg' }],
  },
  summaryDateTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  summaryDateCount: {
    marginTop: 1,
    fontSize: 11.5,
    color: MUTED,
    fontFamily: FONT,
  },
  summaryKidList: {
    marginLeft: 28,
    marginTop: 2,
    marginBottom: 6,
    paddingLeft: 12,
    borderLeftWidth: 1,
    borderLeftColor: HAIRLINE,
  },
  summaryKidRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingVertical: 5,
  },
  summaryKidAvatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: CHIP_BG,
  },
  summaryKidTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  summaryKidName: {
    fontSize: 12.5,
    color: INK,
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  summaryKidMeta: {
    fontSize: 11,
    color: MUTED,
    fontFamily: FONT,
  },
  summaryCheckedInPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: '#EAF3DC',
  },
  summaryCheckedInText: {
    fontSize: 10,
    color: GREEN_INK,
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  summaryKidFootnote: {
    marginTop: 6,
    fontSize: 11,
    color: MUTED,
    fontFamily: FONT,
  },
  summaryDateLabel: {
    flex: 1,
    fontSize: 13,
    color: '#4A4A55',
    fontFamily: FONT,
  },
  summaryDateAmount: {
    fontSize: 13,
    color: INK,
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  summaryDateMuted: {
    color: MUTED,
  },
  summaryDivider: {
    height: 1,
    backgroundColor: HAIRLINE,
    marginTop: 14,
  },
  summarySubRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  summarySubLabel: {
    fontSize: 12.5,
    color: MUTED,
    fontFamily: FONT,
  },
  summarySubValue: {
    fontSize: 12.5,
    color: '#4A4A55',
    fontFamily: FONT,
  },
  summaryPaidValue: {
    color: GREEN_INK,
  },
  summaryTotalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
  },
  summaryTotalLabel: {
    fontSize: 14,
    color: INK,
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  summaryTotalValue: {
    fontSize: 19,
    color: GREEN_INK,
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },

  newRegistrationWrap: {
    paddingHorizontal: 12,
    paddingTop: 4,
    paddingBottom: 12,
  },
  newRegistrationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: GREEN,
  },
  newRegistrationButtonHovered: {
    backgroundColor: '#98BF2F',
  },
  newRegistrationButtonPressed: {
    backgroundColor: '#8BB029',
  },
  newRegistrationTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  newRegistrationTitle: {
    fontSize: 14.5,
    color: '#fff',
    fontFamily: FONT_BOLD,
    fontWeight: 'bold',
  },
  newRegistrationSubtitle: {
    marginTop: 2,
    fontSize: 12,
    lineHeight: 17,
    color: 'rgba(255,255,255,0.88)',
    fontFamily: FONT,
  },

  dateSectionToday: {
    borderColor: GREEN,
    borderWidth: 2,
    backgroundColor: '#F6FAEF',
  },
  dateSectionNext: {
    borderColor: '#C6DFA1',
    backgroundColor: '#FBFDF7',
  },
  dateSectionPast: {
    backgroundColor: '#FAFAFC',
    borderColor: '#F0F0F5',
  },
  dayChipToday: {
    backgroundColor: GREEN,
    borderColor: GREEN,
  },
  dayChipNext: {
    backgroundColor: '#EAF3DC',
    borderColor: '#C6DFA1',
  },
  dayChipPast: {
    backgroundColor: '#F3F3F7',
    borderColor: '#E8E8EF',
  },
  dayChipTextToday: {
    color: '#fff',
  },
  dayChipTextPast: {
    color: MUTED,
  },
});

export default ProfileEventDetails;
